import * as Icons from "heroicons-react";
import Swal from "sweetalert2";
import { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Toaster, toast } from "react-hot-toast";

import axios from "axios";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import {
  useAddCustomerGroupMutation,
  useCustomerGroupsQuery,
  useDeleteCustomerGroupMutation,
  useUpdateCustomerGroupMutation,
} from "../../services/customerGroupApi";
import Header from "../Common/Header/Header";
import LoadingModal from "../Common/Modal/LoadingModal";
import SideBar from "../Common/SideBar/SideBar";
import { signInUser } from "../Utility/Auth";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api/";

const CustomerGroup = () => {
  const lang = useSelector((state) => state.languageReducer);

  const user = signInUser();
  const { aamarId } = user;
  const [loader, setLoader] = useState(false);
  const [isEditing, setIsEditing] = useState(false); // Track if editing or creating
  const [selectedGroup, setSelectedGroup] = useState(null); // Store selected group for editing
  const [cGroupCode, setCGroupCode] = useState(); //unique code state
  // Handle modal show and hide
  const [showModal, setShowModal] = useState(false);
  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditing(false); // Reset the edit state when modal is closed
    setSelectedGroup(null);
    reset();
  };
  const handleLoaderClose = () => {
    setLoader(false);
  };

  // Queries and Mutations
  const { data: groups = [] } = useCustomerGroupsQuery();
  const [addGroup] = useAddCustomerGroupMutation();
  const [deleteGroup] = useDeleteCustomerGroupMutation();
  const [updateGroup] = useUpdateCustomerGroupMutation();

  // useForm setup
  const {
    register,
    handleSubmit,
    reset,
    setValue, // To set field values when editing
    formState: { errors },
  } = useForm({
    defaultValues: {
      status: "active",
    },
  });

  // CHECK UNIQUE
  const checkCodeInAPI = async (code) => {
    try {
      const baseUrl = BASE_URL.endsWith("/") ? BASE_URL : `${BASE_URL}/`;
      const response = await axios.get(
        `${baseUrl}customerGroup/unique/${code}/${aamarId}`,
        {
          params: { code },
        }
      );
      // console.log(response.data.exists);
      return response.data?.exists || false;
    } catch (error) {
      console.error("Error checking code in API:", error);
      return false; // Default to not found in case of an error
    }
  };

  const generateUniqueSixDigitCode = async () => {
    let numericCode;
    do {
      const uuid = uuidv4();
      numericCode = parseInt(uuid.split("-")[0], 16) % 1000000;
      numericCode = String(numericCode).padStart(6, "0");
    } while (await checkCodeInAPI(numericCode));

    setCGroupCode(numericCode);
    return numericCode;
  };

  useEffect(() => {
    generateUniqueSixDigitCode();
  }, []);

  // console.log("aamarId", aamarId);
  // Handle form submit
  const onSubmit = async (data) => {
    const generatedCode = await generateUniqueSixDigitCode();
    setLoader(true);
    try {
      // Combine form data with `aamarId`
      const groupData = { ...data, code: generatedCode, aamarId };

      if (isEditing && selectedGroup) {
        const updatedData = { ...groupData, _id: selectedGroup._id };
        // Call the mutation for updating
        await updateGroup(updatedData).unwrap();

        toast.success("Group updated successfully!");
      } else {
        // Call the mutation for adding a new group
        await addGroup(groupData).unwrap();

        toast.success("Group added successfully!");
      }

      // Reset form, close modal, and clear editing state
      reset();
      setIsEditing(false);
      handleCloseModal();
    } catch (error) {
      console.error("Error during submission:", error);
      toast.error(
        isEditing ? "Failed to update group." : "Failed to add group."
      );
    } finally {
      setLoader(false);
      setSelectedGroup(null);
    }
  };

  // console.log("selectedGroup", selectedGroup);

  // Handle edit button
  const handleEdit = (group) => {
    setIsEditing(true);
    setSelectedGroup(group);
    setValue("name", group.name); // Pre-fill form fields
    setValue("discount", group.discount);
    setValue("code", group.code);
    setValue("status", group.status);
    setShowModal(true);
  };

  // handle delete button
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Are you sure you want to delete this group?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      setLoader(true);
      try {
        await deleteGroup(id).unwrap();
        toast.success("Group deleted successfully!");
      } catch (error) {
        console.error("Error during deletion:", error);
        toast.error("Failed to delete group.");
      } finally {
        setLoader(false);
      }
    }
  };

  // console.log("groups", groups);

  return (
    <div>
      <div className="container-fluid">
        <LoadingModal
          title={"Please Wait"}
          onShow={loader}
          handleClose={handleLoaderClose}
        ></LoadingModal>

        <div className="row">
          <div className="col-md-2">
            <SideBar />
          </div>

          <div className="col-md-10">
            <Header title={lang?.customerGroup} />
            <div className="d-flex justify-content-end m-2">
              <Button variant="dark" onClick={handleShowModal}>
                {isEditing ? "Edit Customer Group" : "+ Add Customer Group"}
              </Button>
            </div>

            <div className="p-3">
              <div className="table-responsive">
                <table className="table table-hover table-striped">
                  <thead className="table-light">
                    <tr>
                      <th className="text-center">#</th>
                      <th className="text-start">Name</th>
                      <th className="text-center">Discount</th>
                      <th className="text-center">Code</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.length > 0 ? (
                      groups.map((group, index) => (
                        <tr key={group._id}>
                          <td className="text-center">{index + 1}</td>
                          <td>{group.name}</td>
                          <td className="text-center">{group.discount}%</td>
                          <td className="text-center">{group.code}</td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center align-items-center gap-2">
                              <Icons.PencilAltOutline
                                className="icon-eye"
                                title="Edit"
                                onClick={() => handleEdit(group)} // Handle edit
                              />
                              <Icons.TrashOutline
                                className="icon-trash"
                                title="Delete"
                                onClick={() => handleDelete(group._id)} // Handle delete
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">
                          No groups available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <Modal show={showModal} onHide={handleCloseModal}>
              <Modal.Header closeButton>
                <Modal.Title>
                  {isEditing ? "Edit Customer Group" : "Add Customer Group"}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="input-group mb-3">
                    <Form.Group className="w-100">
                      <Form.Label>Group Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter group name"
                        {...register("name", {
                          required: "Group name is required",
                        })}
                      />
                    </Form.Group>
                  </div>

                  <div className="input-group mb-3">
                    <Form.Group className="w-100">
                      <Form.Label>Discount (%)</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="Enter discount percentage"
                        {...register("discount", {
                          required: "Discount is required",
                        })}
                      />
                    </Form.Group>
                  </div>

                  <div className="row">
                    <div className="col-6">
                      <Form.Group className="w-100">
                        <Form.Label>Code</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter group code"
                          {...register("code", {
                            required: "Code is required",
                          })}
                          value={cGroupCode}
                          readOnly
                        />
                      </Form.Group>
                    </div>

                    <div className="mb-3 col-6">
                      <Form.Group>
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                          {...register("status", { required: true })}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </Form.Select>
                      </Form.Group>
                    </div>
                  </div>

                  <Button variant="dark" type="submit" className="w-100">
                    <Icons.Save size={18} className="me-2" />
                    {isEditing ? "Update Group" : "Save Group"}
                  </Button>
                </form>
              </Modal.Body>
            </Modal>
          </div>
        </div>
      </div>

      <Toaster position="bottom-right" />
    </div>
  );
};

export default CustomerGroup;
